import { useCallback, useEffect, useRef, useState } from 'react';

import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import { closeHistory } from '@atlaskit/editor-prosemirror/history';
import { Slice, type Fragment, type Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import { replaceConfluenceTitle } from '../../../ui/prebuilt/config-items/actions/replace-title';
import { type EditorPluginAIConfigItem } from '../../../ui/prebuilt/config-items/config-items';
import { isSuggestTitleForConfluence } from '../../../ui/prebuilt/config-items/utils/suggest-title';
import {
	type AIExperienceMachineContext,
	type Positions,
} from '../../../ui/utils/xstate/get-ai-experience-service';
import { startWatchingAnnotationChanges } from '../../ai-annotation-watchmen/transforms';
import { clearSelectionDecorations } from '../../decoration/actions';
import { inEditorDirectStreamingPluginKey } from '../in-editor-direct-streaming-plugin-key';
import { initPositions, updateLoadingPositions, updateStreamPositions } from '../transforms';

const SCROLL_MARGIN = 32;

function getLastContentDepth(fragment: Fragment) {
	let depth = 0;
	let currentNode: Fragment | PMNode = fragment;

	while (currentNode.lastChild) {
		depth++;
		currentNode = currentNode.lastChild;
	}

	return depth;
}

export const useInEditorDirectStreaming = ({
	configItem,
	view,
	positions,
	modalRef,
}: {
	configItem: EditorPluginAIConfigItem;
	view: EditorView;
	positions: Positions;
	modalRef?: React.RefObject<HTMLDivElement>;
}) => {
	const isSuggestTitle = isSuggestTitleForConfluence(configItem);
	const showTelepointer = !isSuggestTitle;

	const [isPaused, setIsPaused] = useState(true);

	const [_start, setStart] = useState<number>();

	const scrollParentRef = useRef<HTMLElement | null>(null);
	const originalUserContentSlice = useRef<Slice>();

	useEffect(() => {
		// The hook may not be flagged so we flag any effects
		if (!fg('platform_editor_ai_in_document_streaming')) {
			return;
		}
		const dom = view.dom;
		const scrollParent = findOverflowScrollParent(dom);
		if (scrollParent) {
			scrollParentRef.current = scrollParent;
		}
	}, [view]);

	useEffect(() => {
		// The hook may not be flagged so we flag any effects
		if (!fg('platform_editor_ai_in_document_streaming')) {
			return;
		}
		if (_start !== undefined && scrollParentRef.current) {
			const startCoords = view.coordsAtPos(_start);
			const offsetTop = view.coordsAtPos(0).top;
			const modalHeight = modalRef?.current?.offsetHeight || 0;

			if (startCoords.top < SCROLL_MARGIN) {
				/**
				 * If the start position is above viewport,
				 * we set the scroll so that the start is at the top of viewport (+ margin)
				 */
				scrollParentRef.current.scroll({
					top: startCoords.top - offsetTop + view.dom.offsetTop - SCROLL_MARGIN,
					behavior: 'instant',
				});
			} else if (
				/**
				 * If the start position is below the viewport,
				 * we set the scroll so that the start is at the bottom of viewport (+ margin)
				 */
				startCoords.top - offsetTop + view.dom.offsetTop + SCROLL_MARGIN >
				scrollParentRef.current.offsetHeight + scrollParentRef.current.scrollTop - modalHeight
			) {
				scrollParentRef.current.scroll({
					top:
						// toPos.bottom - 0.top is height of content
						startCoords.bottom -
						offsetTop +
						// offset of view dom from scroll parent
						view.dom.offsetTop -
						// subtracting offsetHeight keeps content at the bottom so
						// auto-scrolling is more incremental
						scrollParentRef.current.offsetHeight +
						// extra margin so text isn't at the very very bottom
						SCROLL_MARGIN +
						// extra margin so modal isn't in the way
						modalHeight,
					behavior: 'instant',
				});
			}
		}
	}, [view, _start, modalRef]);

	const isEmptySelection = positions[0] === positions[1];
	const shouldReplaceDoc = isEmptySelection && !configItem.insertOnEmptySelection;
	const [start, end] = shouldReplaceDoc ? [1, view.state.doc.content.size - 1] : positions;

	/**
	 * we should show highlight when:
	 * - the selection is not empty
	 * - the selection is empty and we're inserting into the current position (i.e. free generate)
	 *
	 * we should not show highlight when:
	 * - suggest title
	 */
	const showHighlight =
		(!isEmptySelection || !!configItem.insertOnEmptySelection) && !isSuggestTitle;

	useEffect(() => {
		// The hook may not be flagged so we flag any effects
		if (!fg('platform_editor_ai_in_document_streaming') || isEmptySelection || !showHighlight) {
			return;
		}
		const min = 80,
			max = 240;
		const prestreamLoading = setInterval(
			() => {
				if (isPaused) {
					return;
				}
				view.dispatch(updateLoadingPositions(view.state.tr));

				if (
					(inEditorDirectStreamingPluginKey.getState(view.state) || {}).aiContentEndPos !==
					undefined
				) {
					clearInterval(prestreamLoading);
				}
			},
			Math.random() * (max - min) + min,
		);

		return () => {
			clearInterval(prestreamLoading);
		};
	}, [view, isPaused, isEmptySelection, showHighlight]);

	const mountDecorations = useCallback(() => {
		const pluginState = inEditorDirectStreamingPluginKey.getState(view.state) || {};
		if (pluginState.startPos === undefined) {
			clearSelectionDecorations(view.state)(view.state, view.dispatch);
			setIsPaused(false);
			/**
			 * Store the original user content slice so that we can restore it
			 * and use it to manipulate the history plugin.
			 */
			originalUserContentSlice.current = view.state.doc.slice(start, end);
			view.dispatch(
				closeHistory(
					initPositions({
						start,
						end,
						showHighlight,
						showTelepointer,
					})(startWatchingAnnotationChanges({ from: start, to: end })(view.state.tr)),
				),
			);
			setStart(start);
			return () => {
				setIsPaused(true);
			};
		}
	}, [view, start, end, showHighlight, showTelepointer]);

	const updateStream = useCallback(
		({
			fragment,
			isFinalStream = false,
			// Prevent scroll-jacking by default to be safe
			hasUserScrolled = true,
			selectionMetaData,
		}: {
			fragment: Fragment;
			isFinalStream?: boolean;
			hasUserScrolled?: boolean;
			selectionMetaData?: AIExperienceMachineContext['selectionMetaData'];
		}) => {
			if (isSuggestTitle) {
				replaceConfluenceTitle({
					editorView: view,
					pmFragment: fragment,
				});
				return;
			}

			const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
			const { startPos, aiContentEndPos, userContentEndPos } = pluginState || {};
			if (startPos === undefined || userContentEndPos === undefined) {
				return;
			}

			const endPositionDepth = view.state.doc.resolve(userContentEndPos).depth;
			const lastFragmentDepth = getLastContentDepth(fragment);
			// selectionMetaData will always be undefined when platform_editor_ai_iw_adf_streaming is disabled
			const sliceOpenStart = selectionMetaData?.sliceOpenStart || 1;
			const sliceOpenEnd =
				selectionMetaData?.sliceOpenEnd || Math.min(endPositionDepth, lastFragmentDepth);
			// slice depth logic based on ui/prebuilt/config-items/utils/action-utils.ts
			let slice = new Slice(fragment, sliceOpenStart, sliceOpenEnd);
			// /**
			//  * Revisit later - occasionally we receive a response with empty content which causes the fragment -> slice
			//  * to have a negative size, which causes the experience to break.
			//  * At the moment, we can fallback to empty slice but user clicking 'replace' won't see any changes.
			//  */
			if (slice.size < 0) {
				slice = Slice.empty;
			}

			const toPos = isFinalStream
				? userContentEndPos
				: Math.min(Math.max(aiContentEndPos || 0, startPos + slice.size + 1), userContentEndPos);

			let tr = view.state.tr;

			if (isFinalStream && originalUserContentSlice.current) {
				/**
				 * Dispatch an extra transaction to reset the document back to original state
				 * so that the history plugin can correctly track the difference between the
				 * original document and the final document after streaming.
				 */
				view.dispatch(
					tr
						.replaceRange(startPos, userContentEndPos, originalUserContentSlice.current)
						.setMeta('addToHistory', false),
				);

				// Now we replace it with the AI response
				tr = view.state.tr.replaceRange(
					startPos,
					startPos + (originalUserContentSlice.current.size || 0),
					slice,
				);
			} else {
				tr = view.state.tr.replaceRange(startPos, toPos, slice).setMeta('addToHistory', false);
			}

			const mappedStartPos = Math.max(0, tr.mapping.map(startPos, -1));
			const mappedToPos = Math.min(tr.doc.content.size, tr.mapping.map(toPos, 1));
			let telepointerPos = mappedToPos;
			tr.doc.nodesBetween(mappedStartPos, mappedToPos, (node, pos) => {
				if (node.isLeaf) {
					telepointerPos = pos + node.nodeSize;
				}
			});
			telepointerPos = Math.min(telepointerPos, mappedToPos);

			tr.setMeta('isAIStreamingTransformation', true)
				.setMeta('isAIFinalStreamingTransformation', isFinalStream)
				.setMeta('scrollIntoView', false);

			view.dispatch(
				updateStreamPositions({
					aiContentEndPos: toPos,
					telepointerPos,
					isFinalStream,
				})(tr),
			);

			if (isFinalStream) {
				view.dispatch(closeHistory(view.state.tr));
			}

			const modalHeight = modalRef?.current?.offsetHeight || 0;
			if (
				!hasUserScrolled &&
				scrollParentRef.current &&
				scrollParentRef.current.offsetHeight - modalHeight < view.coordsAtPos(toPos).bottom
			) {
				scrollParentRef.current.scroll({
					top:
						// toPos.bottom - 0.top is height of content
						view.coordsAtPos(toPos).bottom -
						view.coordsAtPos(0).top +
						// offset of view dom from scroll parent
						view.dom.offsetTop -
						// subtracting offsetHeight keeps content at the bottom so
						// auto-scrolling is more incremental
						scrollParentRef.current.offsetHeight +
						// extra margin so text isn't at the very very bottom
						SCROLL_MARGIN +
						// extra margin so modal isn't in the way
						modalHeight,
					behavior: 'instant',
				});
			}
		},
		[view, modalRef, isSuggestTitle],
	);

	return {
		mountDecorations,
		updateStream,
	};
};
